import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome"
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import MailOutlineIcon from "@mui/icons-material/MailOutline"
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline"
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone"
import CloseIcon from "@mui/icons-material/Close"
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"

import Button from "@components/button"
import {
  GRAY_COLOR,
  LIGHT_BLUE,
  PRIMARY,
  WATSAPP_ACTIVE
} from "@constants/colors"
import { useChangelogPageContext } from "@contexts/main/changelog_page_context"
import {
  ChangelogChannel,
  ChangelogStatus,
  ChangelogTranslations
} from "@models/changelog"
import {
  createAnnouncementReq,
  publishAnnouncementReq,
  translateAnnouncementReq,
  unpublishAnnouncementReq,
  updateAnnouncementReq,
  uploadAnnouncementMediaReq
} from "@domain/changelog"
import { showErrorToast, showSuccessToast } from "@utils/toast"

import { langFlag } from "../shared"

const CHANNEL_META: {
  key: ChangelogChannel
  labelKey: string
  icon: JSX.Element
}[] = [
  {
    key: ChangelogChannel.EMAIL,
    labelKey: "changelog_channel_email",
    icon: <MailOutlineIcon fontSize="small" />
  },
  {
    key: ChangelogChannel.WHATSAPP,
    labelKey: "changelog_channel_whatsapp",
    icon: <ChatBubbleOutlineIcon fontSize="small" />
  },
  {
    key: ChangelogChannel.PUSH,
    labelKey: "changelog_channel_push",
    icon: <NotificationsNoneIcon fontSize="small" />
  }
]

const QUILL_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "image", "blockquote"],
  ["clean"]
]

// Quill emits "<p><br></p>" when empty — treat that as blank.
const isBlankHtml = (html?: string) =>
  !html || !html.replace(/<[^>]*>/g, "").trim()

export default function ChangelogDrawer() {
  const {
    translate,
    settings,
    categories,
    isAnnouncementDrawerOpen,
    editingAnnouncement,
    closeAnnouncementDrawer,
    refetchAnnouncements,
    refetchCategories,
    openSendModal
  } = useChangelogPageContext()

  const supportedLanguages = useMemo(
    () => settings?.supportedLanguages ?? ["tr", "en"],
    [settings]
  )

  const [activeLang, setActiveLang] = useState(supportedLanguages[0])
  const [translations, setTranslations] = useState<ChangelogTranslations>({})
  const [categoryId, setCategoryId] = useState<string>("")
  const [channels, setChannels] = useState<ChangelogChannel[]>([])
  const [coverImageUrl, setCoverImageUrl] = useState<string>("")
  const [uploadingCover, setUploadingCover] = useState(false)
  const [translating, setTranslating] = useState(false)
  const [saving, setSaving] = useState(false)
  // Draft/Published status chosen in the drawer (item ④) + whether to fire the
  // channel notification flow (item ③).
  const [status, setStatus] = useState<ChangelogStatus>(ChangelogStatus.DRAFT)
  const [sendNotification, setSendNotification] = useState(false)

  const wasPublished = editingAnnouncement?.status === ChangelogStatus.PUBLISHED

  const quillRef = useRef<ReactQuill>(null)

  // Quill's image button: upload the picked file to S3 and insert its URL (NOT a
  // huge base64 blob) so the image persists and renders on the front end.
  const imageHandler = useCallback(() => {
    const input = document.createElement("input")
    input.setAttribute("type", "file")
    input.setAttribute("accept", "image/png,image/jpeg,image/gif,image/webp")
    input.click()
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) {
        return
      }
      const res = await uploadAnnouncementMediaReq(file)
      const editor = quillRef.current?.getEditor()
      if (res.isSuccess && res.data?.url && editor) {
        const range = editor.getSelection(true)
        const index = range ? range.index : editor.getLength()
        editor.insertEmbed(index, "image", res.data.url, "user")
        editor.setSelection(index + 1, 0)
      } else {
        showErrorToast(translate("changelog_media_upload_failed"))
      }
    }
  }, [translate])

  const quillModules = useMemo(
    () => ({
      toolbar: { container: QUILL_TOOLBAR, handlers: { image: imageHandler } }
    }),
    [imageHandler]
  )

  // Hydrate form when the drawer opens (create → empty, edit → existing).
  // Reset on close too, so a later "New" never shows the previous announcement.
  useEffect(() => {
    if (!isAnnouncementDrawerOpen) {
      setTranslations({})
      setCategoryId("")
      setChannels([])
      setCoverImageUrl("")
      setStatus(ChangelogStatus.DRAFT)
      setSendNotification(false)
      return
    }
    if (editingAnnouncement) {
      setTranslations(editingAnnouncement.translations || {})
      setCategoryId(editingAnnouncement.categoryId || "")
      setChannels(editingAnnouncement.channels || [])
      setCoverImageUrl(editingAnnouncement.coverImageUrl || "")
      // Only Draft/Published are selectable; scheduled/archived edits open as Draft.
      setStatus(
        editingAnnouncement.status === ChangelogStatus.PUBLISHED
          ? ChangelogStatus.PUBLISHED
          : ChangelogStatus.DRAFT
      )
    } else {
      setTranslations({})
      setCategoryId("")
      setChannels([])
      setCoverImageUrl("")
      setStatus(ChangelogStatus.DRAFT)
    }
    setActiveLang(supportedLanguages[0])
    setSendNotification(false)
  }, [isAnnouncementDrawerOpen, editingAnnouncement, supportedLanguages])

  const setField = (
    lang: string,
    field: "title" | "content" | "shortDescription",
    value: string
  ) => {
    setTranslations((prev) => {
      const current = prev[lang] || { title: "", content: "" }
      return { ...prev, [lang]: { ...current, [field]: value } }
    })
  }

  const langComplete = (lang: string) => {
    const t = translations[lang]
    return !!t?.title?.trim() && !isBlankHtml(t?.content)
  }

  const allLangsComplete = supportedLanguages.every(langComplete)

  const toggleChannel = (key: ChangelogChannel) => {
    setChannels((prev) =>
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
    )
  }

  // Single cover image: upload the picked file to S3, store its URL.
  const onCoverSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = "" // allow re-selecting the same file
    if (!file) {
      return
    }
    setUploadingCover(true)
    try {
      const res = await uploadAnnouncementMediaReq(file)
      if (res.isSuccess && res.data?.url) {
        setCoverImageUrl(res.data.url)
      } else {
        showErrorToast(translate("changelog_media_upload_failed"))
      }
    } finally {
      setUploadingCover(false)
    }
  }

  const removeCover = () => setCoverImageUrl("")

  // AI: fill the other supported languages from the Turkish title + content.
  const onAiTranslate = async () => {
    const source = translations.tr
    if (!source?.title?.trim() || !source?.content?.trim()) {
      showErrorToast(translate("changelog_ai_translate_need_tr"))
      return
    }
    const targets = supportedLanguages.filter((lang) => lang !== "tr")
    if (!targets.length) {
      return
    }
    setTranslating(true)
    try {
      const res = await translateAnnouncementReq({
        title: source.title,
        content: source.content,
        shortDescription: source.shortDescription,
        targetLanguages: targets
      })
      if (res.isSuccess && res.data) {
        const translated = res.data
        setTranslations((prev) => {
          const next = { ...prev }
          Object.entries(translated).forEach(([lang, value]) => {
            next[lang] = {
              ...next[lang],
              title: value.title,
              content: value.content,
              ...(value.shortDescription !== undefined
                ? { shortDescription: value.shortDescription }
                : {})
            }
          })
          return next
        })
        showSuccessToast(translate("changelog_ai_translate_done"))
      } else {
        showErrorToast(translate("changelog_ai_translate_failed"))
      }
    } finally {
      setTranslating(false)
    }
  }

  // Persist content only. `notify` is forwarded to updateAnnouncement solely to
  // re-send an already-published announcement; create always starts as DRAFT.
  const persist = async (notify?: boolean): Promise<string | undefined> => {
    setSaving(true)
    try {
      if (editingAnnouncement) {
        const res = await updateAnnouncementReq({
          _id: editingAnnouncement._id,
          categoryId: categoryId || undefined,
          coverImageUrl: coverImageUrl || undefined,
          channels,
          translations,
          notify
        })
        if (!res.isSuccess) {
          showErrorToast(res.errorMessage || translate("changelog_save_failed"))
          return undefined
        }
        return editingAnnouncement._id
      }
      const res = await createAnnouncementReq({
        categoryId,
        coverImageUrl: coverImageUrl || undefined,
        channels,
        translations
      })
      if (!res.isSuccess) {
        showErrorToast(res.errorMessage || translate("changelog_save_failed"))
        return undefined
      }
      return res.data?._id
    } finally {
      setSaving(false)
    }
  }

  // Single Create/Update action. Status (Draft/Published) drives the lifecycle;
  // the "Send notification" toggle drives the channel dispatch flow.
  const onSave = async () => {
    const willPublish = status === ChangelogStatus.PUBLISHED
    if (willPublish && !allLangsComplete) {
      showErrorToast(translate("changelog_incomplete_langs"))
      return
    }
    if (!categoryId) {
      showErrorToast(translate("changelog_pick_category"))
      return
    }
    if (sendNotification && channels.length === 0) {
      showErrorToast(translate("changelog_pick_channel"))
      return
    }

    // Re-notifying an already-published announcement is done via update(notify:true).
    const reNotify = willPublish && wasPublished && sendNotification
    const id = await persist(reNotify ? true : undefined)
    if (!id) {
      return
    }
    refetchAnnouncements()
    refetchCategories()

    // Target = Draft → make sure it is not published.
    if (!willPublish) {
      if (wasPublished) {
        const res = await unpublishAnnouncementReq(id)
        if (!res.isSuccess) {
          showErrorToast(
            typeof res.errorMessage === "string"
              ? res.errorMessage
              : translate("changelog_save_failed")
          )
          return
        }
      }
      showSuccessToast(translate("changelog_saved_draft"))
      closeAnnouncementDrawer()
      return
    }

    // Target = Published, first publish (was Draft/Scheduled).
    if (!wasPublished) {
      if (sendNotification) {
        // Pick channel + timing in the send modal, which publishes + notifies.
        closeAnnouncementDrawer()
        openSendModal({
          ...(editingAnnouncement || ({} as never)),
          _id: id,
          categoryId,
          channels,
          translations,
          status: ChangelogStatus.PUBLISHED as never
        })
        return
      }
      // Silent publish → shows in the feed, no channel messages.
      const res = await publishAnnouncementReq({ _id: id, notify: false })
      if (!res.isSuccess) {
        showErrorToast(res.errorMessage || translate("changelog_save_failed"))
        return
      }
      showSuccessToast(translate("changelog_published"))
      closeAnnouncementDrawer()
      return
    }

    // Already published, staying published — persist handled content (+ re-notify).
    showSuccessToast(
      reNotify ? translate("changelog_sent") : translate("changelog_saved")
    )
    closeAnnouncementDrawer()
  }

  return (
    <Drawer
      anchor="right"
      open={isAnnouncementDrawerOpen}
      onClose={closeAnnouncementDrawer}
      PaperProps={{ sx: { width: ["100vw", "40rem"], maxWidth: "40rem" } }}
    >
      <Stack sx={{ height: "100%" }}>
        {/* header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700 }}>
            {editingAnnouncement
              ? translate("changelog_edit_announcement")
              : translate("changelog_new_announcement_title")}
          </Typography>
          <IconButton onClick={closeAnnouncementDrawer} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* body */}
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          {/* language tabs + AI translate */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{ mb: 2.5 }}
          >
            <Stack direction="row" gap={1} flexWrap="wrap">
              {supportedLanguages.map((lang) => {
                const isActive = activeLang === lang
                const complete = langComplete(lang)
                return (
                  <Stack
                    key={lang}
                    direction="row"
                    alignItems="center"
                    gap={0.5}
                    onClick={() => setActiveLang(lang)}
                    sx={{
                      cursor: "pointer",
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: isActive ? PRIMARY : "divider",
                      background: isActive ? LIGHT_BLUE : "white"
                    }}
                  >
                    <span>{langFlag(lang)}</span>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                      {lang === "tr"
                        ? "Türkçe"
                        : lang === "en"
                          ? "English"
                          : lang.toUpperCase()}
                    </Typography>
                    {!complete && (
                      <ErrorOutlineIcon
                        sx={{ fontSize: "0.9rem", color: "#E0A100" }}
                      />
                    )}
                  </Stack>
                )
              })}
            </Stack>

            <Tooltip title={translate("changelog_ai_translate")}>
              <span>
                <IconButton
                  onClick={onAiTranslate}
                  disabled={translating}
                  sx={{ color: PRIMARY }}
                >
                  {translating ? (
                    <CircularProgress size={20} />
                  ) : (
                    <AutoAwesomeIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <FieldLabel text={translate("changelog_field_title")} />
          <TextField
            fullWidth
            size="small"
            value={translations[activeLang]?.title || ""}
            onChange={(e) => setField(activeLang, "title", e.target.value)}
            placeholder={translate("changelog_field_title_ph")}
            sx={{ mb: 2.5 }}
          />

          <FieldLabel text={translate("changelog_field_short_desc")} />
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            maxRows={4}
            value={translations[activeLang]?.shortDescription || ""}
            onChange={(e) =>
              setField(activeLang, "shortDescription", e.target.value)
            }
            placeholder={translate("changelog_field_short_desc_ph")}
            helperText={translate("changelog_field_short_desc_hint")}
            sx={{ mb: 2.5 }}
          />

          <FieldLabel text={translate("changelog_field_content")} />
          <Box
            sx={{
              mb: 2.5,
              "& .ql-container": {
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
                fontSize: "0.95rem"
              },
              "& .ql-toolbar": {
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8
              },
              "& .ql-editor": { minHeight: "9rem" }
            }}
          >
            <ReactQuill
              key={activeLang}
              ref={quillRef}
              theme="snow"
              modules={quillModules}
              value={translations[activeLang]?.content || ""}
              onChange={(value) => setField(activeLang, "content", value)}
              placeholder={translate("changelog_field_content_ph")}
            />
          </Box>

          <FieldLabel text={translate("changelog_field_cover")} />
          {coverImageUrl ? (
            <Box
              sx={{
                position: "relative",
                mb: 2.5,
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider"
              }}
            >
              <Box
                component="img"
                src={coverImageUrl}
                alt="cover"
                sx={{
                  width: "100%",
                  maxHeight: "14rem",
                  objectFit: "cover",
                  display: "block"
                }}
              />
              <IconButton
                size="small"
                onClick={removeCover}
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  background: "rgba(0,0,0,0.55)",
                  color: "white",
                  ":hover": { background: "rgba(0,0,0,0.75)" }
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </Box>
          ) : (
            <Box
              component="label"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 2,
                py: 4,
                mb: 2.5,
                cursor: "pointer",
                color: GRAY_COLOR,
                overflow: "hidden"
              }}
            >
              {uploadingCover ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <CloudUploadOutlinedIcon />
                  <Typography sx={{ fontSize: "0.85rem" }}>
                    {translate("changelog_field_cover_hint")}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem" }}>PNG, JPG</Typography>
                </>
              )}
              <input
                hidden
                type="file"
                accept="image/png,image/jpeg"
                onChange={onCoverSelect}
              />
            </Box>
          )}

          <FieldLabel text={translate("changelog_field_category")} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
              mb: 2.5
            }}
          >
            {categories.map((c) => {
              const isActive = categoryId === c._id
              const name =
                c.translations?.tr?.name ||
                Object.values(c.translations || {})[0]?.name
              return (
                <Stack
                  key={c._id}
                  direction="row"
                  alignItems="center"
                  gap={1}
                  onClick={() => setCategoryId(c._id)}
                  sx={{
                    cursor: "pointer",
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: isActive ? PRIMARY : "divider",
                    background: isActive ? LIGHT_BLUE : "white"
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: c.color
                    }}
                  />
                  <Typography sx={{ fontSize: "0.85rem" }}>{name}</Typography>
                </Stack>
              )
            })}
          </Box>

          <FieldLabel text={translate("changelog_field_channels")} />
          <Stack direction="row" gap={1} sx={{ mb: 2.5 }}>
            {CHANNEL_META.map((ch) => {
              const isActive = channels.includes(ch.key)
              return (
                <Stack
                  key={ch.key}
                  direction="row"
                  alignItems="center"
                  gap={0.75}
                  onClick={() => toggleChannel(ch.key)}
                  sx={{
                    cursor: "pointer",
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: isActive ? PRIMARY : "divider",
                    background: isActive ? LIGHT_BLUE : "white",
                    color: isActive ? PRIMARY : "text.primary",
                    "& svg": {
                      color:
                        ch.key === ChangelogChannel.WHATSAPP && isActive
                          ? WATSAPP_ACTIVE
                          : "inherit"
                    }
                  }}
                >
                  {ch.icon}
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                    {translate(ch.labelKey)}
                  </Typography>
                </Stack>
              )
            })}
          </Stack>

          {!allLangsComplete && (
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                background: "#FEF7E6",
                border: "1px solid #F5E1B0",
                color: "#8A6D1B"
              }}
            >
              <ErrorOutlineIcon fontSize="small" />
              <Typography sx={{ fontSize: "0.82rem" }}>
                {translate("changelog_incomplete_langs")}
              </Typography>
            </Stack>
          )}

          {/* Status (Draft/Published) + Send-notification toggle (items ③④) */}
          <Stack gap={1.5} sx={{ mt: 3 }}>
            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 0.5 }}>
                {translate("changelog_status_label")}
              </Typography>
              <Select
                size="small"
                fullWidth
                value={status}
                onChange={(event) =>
                  setStatus(event.target.value as ChangelogStatus)
                }
              >
                <MenuItem value={ChangelogStatus.DRAFT}>
                  {translate("changelog_status_draft")}
                </MenuItem>
                <MenuItem value={ChangelogStatus.PUBLISHED}>
                  {translate("changelog_status_published")}
                </MenuItem>
              </Select>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={sendNotification}
                  onChange={(event) =>
                    setSendNotification(event.target.checked)
                  }
                />
              }
              label={translate("changelog_send_notification")}
            />
          </Stack>
        </Box>

        {/* footer */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}
        >
          <Button
            color="secondary"
            variant="text"
            sx={{ minWidth: "5rem", background: "transparent" }}
            onClick={closeAnnouncementDrawer}
          >
            {translate("cancel")}
          </Button>
          <Button loading={saving} onClick={onSave}>
            {editingAnnouncement
              ? translate("changelog_update_btn")
              : translate("changelog_create_btn")}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  )
}

function FieldLabel({ text }: { text: string }) {
  return (
    <Typography
      sx={{
        fontSize: "0.82rem",
        fontWeight: 600,
        mb: 0.75,
        color: "text.primary"
      }}
    >
      {text}
    </Typography>
  )
}
